import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr, platformApiOrigin } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save } from "lucide-react";
import { useMerchantAuth } from "@/lib/auth";

export default function AdminGokwikCheckout() {
  const { tenant } = useMerchantAuth();
  const [active, setActive] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [cartApi, setCartApi] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/merchant/config/gokwik-checkout").then(({ data }) => {
      setActive(Boolean(data.active));
      setCfg(data.gokwik_checkout || { mid: "", app_id: "", sandbox: false, has_app_secret: false });
      setCartApi(data.cart_api_base || "");
    }).catch((e) => toast.error(extractErr(e)));
  }, []);

  if (!cfg) return <div className="text-slate-500">Loading…</div>;
  if (!active) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Gokwik Checkout</strong> from the App Store to replace native checkout with GoKwik.</Card>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        mid: cfg.mid,
        app_id: cfg.app_id,
        sandbox: Boolean(cfg.sandbox),
      };
      if (cfg.app_secret) payload.app_secret = cfg.app_secret;
      const { data } = await api.put("/merchant/config/gokwik-checkout", payload);
      setCfg({ ...data.gokwik_checkout, app_secret: "" });
      setCartApi(data.cart_api_base || cartApi);
      toast.success("GoKwik Checkout saved");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  const origin = platformApiOrigin() || (typeof window !== "undefined" ? window.location.origin : "");
  const apiBase = cartApi || `${origin}/api/gokwik/${tenant?.slug || "your-store"}/gokwik/v1`;

  return (
    <div className="max-w-2xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="gk-back"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Gokwik Checkout</h1>
        <p className="text-sm text-slate-500 mt-1">When this addon is on and keys are saved, cart and checkout open GoKwik instead of the native payment form. INR only. Turning this on switches off Shiprocket Fastrr Checkout.</p>
      </div>
      <Card className="p-6 space-y-5 border-slate-200/70">
        <div>
          <Label htmlFor="gk-mid">Merchant ID (MID)</Label>
          <Input id="gk-mid" className="mt-1 font-mono" data-testid="gk-mid" value={cfg.mid || ""} onChange={(e) => setCfg({ ...cfg, mid: e.target.value })} placeholder="From the GoKwik merchant panel" />
        </div>
        <div>
          <Label htmlFor="gk-app-id">App ID</Label>
          <Input id="gk-app-id" className="mt-1 font-mono" data-testid="gk-app-id" value={cfg.app_id || ""} onChange={(e) => setCfg({ ...cfg, app_id: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="gk-app-secret">App Secret</Label>
          <Input id="gk-app-secret" type="password" className="mt-1 font-mono" data-testid="gk-app-secret" value={cfg.app_secret || ""} onChange={(e) => setCfg({ ...cfg, app_secret: e.target.value })} placeholder={cfg.has_app_secret ? "Saved — leave blank to keep" : "Paste App Secret"} />
          <p className="text-xs text-slate-500 mt-1">{cfg.has_app_secret ? "A secret is already stored. Leave blank to keep it." : "Required for GoKwik to load your cart."}</p>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="gk-sandbox">Sandbox mode</Label>
            <p className="text-xs text-slate-500 mt-0.5">Use GoKwik sandbox SDK and test MID. Turn off for live orders.</p>
          </div>
          <Switch id="gk-sandbox" checked={Boolean(cfg.sandbox)} onCheckedChange={(sandbox) => setCfg({ ...cfg, sandbox })} data-testid="gk-sandbox" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 space-y-1" data-testid="gk-api-url">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Give this URL to GoKwik (custom / Woo-style cart API)</div>
          <code className="text-xs break-all text-slate-800">{apiBase}</code>
          <p className="text-xs text-slate-500">Cart endpoint: <span className="font-mono">{apiBase}/cart</span>. Headers: <span className="font-mono">appid</span> and <span className="font-mono">appsecret</span>.</p>
        </div>
        <Button type="button" onClick={save} disabled={saving} data-testid="gk-save">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
        </Button>
      </Card>
    </div>
  );
}
